import { useState, useEffect } from 'react';
import { Modal } from '@/components';
import clsx from 'clsx';
import { formatDisplayDateTime } from '@/utils/Date';
import {
  FeedbackStatus,
  FeedbackType,
  ModuleType,
} from '@/types/feedback.types';
import {
  useGetFeedbackByIdQuery,
  useUpdateFeedbackStatusMutation,
} from '@/services/UserFeedbackService';

interface FeedbackDetailsModalProps {
  feedbackId: string;
  mode: 'view' | 'edit';
  open: boolean;
  onClose: () => void;
}

const feedbackTypeMap: Record<FeedbackType, string> = {
  [FeedbackType.ASK_QUESTION]: 'Ask Question',
  [FeedbackType.LEAVE_COMMENT]: 'Leave Comment',
  [FeedbackType.REPORT_BUG]: 'Report Bug',
  [FeedbackType.SUGGEST_IMPROVEMENT]: 'Suggest Improvement',
  [FeedbackType.GENERAL_FEEDBACK]: 'General Feedback',
  [FeedbackType.PRAISE]: 'Praise',
};

const moduleTypeMap: Record<ModuleType, string> = {
  [ModuleType.HARBOR_BOOKING]: 'Harbor Booking',
  [ModuleType.POI]: 'Points of Interest',
  [ModuleType.ROUTING]: 'Routing',
  [ModuleType.GENERAL]: 'General',
};

const feedbackStatusMap: Record<
  FeedbackStatus,
  { label: string; variant: string }
> = {
  [FeedbackStatus.PENDING]: { label: 'Pending', variant: 'warning' },
  [FeedbackStatus.IN_PROGRESS]: { label: 'In Progress', variant: 'primary' },
  [FeedbackStatus.RESOLVED]: { label: 'Resolved', variant: 'success' },
  [FeedbackStatus.CLOSED]: { label: 'Closed', variant: 'info' },
  [FeedbackStatus.REJECTED]: { label: 'Rejected', variant: 'danger' },
};

const FeedbackDetailsModal = ({
  feedbackId,
  mode,
  open,
  onClose,
}: FeedbackDetailsModalProps) => {
  const {
    data: feedback,
    isLoading,
    isError,
  } = useGetFeedbackByIdQuery(feedbackId);
  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateFeedbackStatusMutation();
  const [newStatus, setNewStatus] = useState<FeedbackStatus | null>(null);
  const [error, setError] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');

  // Initialize newStatus when feedback loads in edit mode
  useEffect(() => {
    if (feedback && mode === 'edit') {
      setNewStatus(feedback.status);

      // Initialize admin notes if available
      if (feedback.admin_notes) {
        setAdminNotes(feedback.admin_notes);
      }
    }
  }, [feedback, mode]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewStatus(parseInt(e.target.value) as FeedbackStatus);
    setError('');
  };

  const hasChanges = () => {
    return (
      (newStatus !== null && newStatus !== feedback?.status) ||
      adminNotes !== (feedback?.admin_notes || '')
    );
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      // Silently ignore when nothing changed
      return;
    }

    try {
      await updateStatus({
        id: feedbackId,
        payload: {
          status: newStatus !== null ? newStatus : feedback!.status,
          admin_notes: adminNotes || null,
        },
      }).unwrap();
      onClose();
    } catch (err) {
      console.warn(err);
      setError('Failed to update feedback. Please try again.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="!flex items-center justify-center"
      aria-labelledby="feedback-details-modal-title"
      aria-describedby="feedback-details-modal-description"
      data-testid="modal"
    >
      <div className="bg-white rounded-xl p-0 w-full max-w-2xl mx-4 shadow-2xl overflow-hidden">
        <div className="bg-primary/10 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-primary">
            {mode === 'view' ? 'View Feedback' : 'Update Feedback Status'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-1.5 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 pb-6 max-h-[90vh] overflow-y-auto">
          <div id="feedback-details-modal-description" className="sr-only">
            {mode === 'view'
              ? 'Modal displaying feedback details in view-only mode'
              : 'Modal allowing status update for feedback'}
          </div>
          {isLoading ? (
            <div className="text-center py-4">Loading feedback details...</div>
          ) : isError || !feedback ? (
            <div className="text-center py-4 text-red-500">
              Failed to load feedback details. Please try again.
            </div>
          ) : (
            <>
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  {feedback.user?.profile_picture ? (
                    <img
                      src={feedback.user.profile_picture}
                      alt={`${feedback.user.name || feedback.user.email}'s profile picture`}
                      className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                      {(feedback.user?.name ||
                        feedback.user?.email ||
                        'U')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {feedback.user?.name || 'Unknown User'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {feedback.user?.email}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Submitted on{' '}
                      {formatDisplayDateTime(new Date(feedback.created_at))}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label
                    htmlFor="feedback-type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Feedback Type
                  </label>
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-800">
                      {feedbackTypeMap[feedback.feedback_type] || 'Unknown'}
                    </span>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="module"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Module
                  </label>
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-800">
                      {feedback.module
                        ? moduleTypeMap[feedback.module]
                        : 'Not specified'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="feedback-content"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message
                </label>
                <div
                  className="bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-200 rounded-lg p-4 min-h-[100px] overflow-y-auto text-gray-700 whitespace-pre-wrap"
                  aria-describedby="feedback-content-description"
                >
                  {feedback.feedback_text || 'No content'}
                </div>
                <p id="feedback-content-description" className="sr-only">
                  Full content of the feedback, not editable
                </p>
              </div>

              {feedback.media && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachment
                  </label>
                  <div className="bg-white overflow-hidden">
                    <img
                      src={feedback.media}
                      alt="Feedback attachment"
                      className="w-36 h-36 object-contain"
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                  Admin Actions
                </h3>

                <label
                  htmlFor="feedback-status"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Status
                </label>
                {mode === 'view' ? (
                  <div className="flex items-center">
                    <div
                      className={clsx(
                        'w-3 h-3 rounded-full mr-2',
                        feedback.status === FeedbackStatus.PENDING &&
                          'bg-yellow-500',
                        feedback.status === FeedbackStatus.IN_PROGRESS &&
                          'bg-blue-500',
                        feedback.status === FeedbackStatus.RESOLVED &&
                          'bg-green-500',
                        feedback.status === FeedbackStatus.CLOSED &&
                          'bg-gray-500',
                        feedback.status === FeedbackStatus.REJECTED &&
                          'bg-red-500'
                      )}
                    ></div>
                    <span className="text-sm font-medium text-gray-800">
                      {feedbackStatusMap[feedback.status]?.label ||
                        'Unknown Status'}
                    </span>
                  </div>
                ) : (
                  <select
                    id="feedback-status"
                    aria-label="Select feedback status"
                    value={newStatus ?? feedback.status}
                    onChange={handleStatusChange}
                    disabled={false}
                    className={clsx(
                      'select bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-300 rounded-lg text-sm w-full !text-gray-800 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
                      error && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'status-error' : undefined}
                  >
                    {Object.entries(feedbackStatusMap).map(
                      ([value, { label }]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </select>
                )}
                {error && (
                  <p id="status-error" className="text-red-500 text-sm mt-1">
                    {error}
                  </p>
                )}

                {/* Admin Notes */}
                <div>
                  <label
                    htmlFor="admin-notes"
                    className="block text-sm font-medium text-gray-700 mb-2 mt-5"
                  >
                    Admin Notes
                  </label>
                  {mode === 'edit' ? (
                    <textarea
                      id="admin-notes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="w-full min-h-[100px] bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-300 rounded-lg p-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Add your notes about this feedback here..."
                    />
                  ) : (
                    <>
                      {feedback.admin_notes ? (
                        <div
                          id="view-admin-notes"
                          className="p-3 bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-200 rounded-lg text-gray-700 min-h-[80px] whitespace-pre-wrap"
                        >
                          {feedback.admin_notes}
                        </div>
                      ) : (
                        <div className="p-3 bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-200 rounded-lg text-gray-500 italic">
                          No admin notes provided
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="border-t border-gray-100 pt-5 mt-6 flex justify-end gap-4">
                {mode === 'view' ? (
                  <button
                    onClick={onClose}
                    className="inline-flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-200 contrast:hover:bg-gray-200 transition-colors shadow-sm"
                    aria-label="Close feedback details modal"
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      onClick={onClose}
                      className="inline-flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-200 contrast:hover:bg-gray-200 transition-colors shadow-sm"
                      aria-label="Cancel status changes"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className={clsx(
                        'inline-flex items-center justify-center px-5 py-2.5 bg-primary border border-transparent rounded-lg text-sm font-medium text-white hover:bg-primary-dark transition-colors shadow-sm',
                        (!hasChanges() || isUpdating) &&
                          'opacity-50 cursor-not-allowed hover:bg-primary'
                      )}
                      disabled={!hasChanges() || isUpdating}
                      aria-label="Save status changes"
                    >
                      {isUpdating ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FeedbackDetailsModal;
